/**
 * Bracket mathematics, kept separate from the format so it can be reasoned
 * about — and tested — on its own.
 */

/** Smallest power of two that can hold `count` participants. */
export function nextPowerOfTwo(count: number): number {
  let size = 2;
  while (size < count) {
    size *= 2;
  }
  return size;
}

/**
 * Classic seeding order: 1 meets the weakest, 2 the second weakest, and the top
 * seeds can only meet in the final. Built by repeatedly mirroring the previous
 * round, which is what produces the familiar 1-8-4-5-2-7-3-6 pattern.
 */
export function seedingOrder(size: number): number[] {
  let order = [1, 2];

  while (order.length < size) {
    const mirror = order.length * 2 + 1;
    const next: number[] = [];
    for (const seed of order) {
      next.push(seed, mirror - seed);
    }
    order = next;
  }

  return order;
}

export function roundCount(size: number): number {
  return Math.log2(size);
}

/** Matches in a round, counting from the first round played. */
export function matchesInRound(size: number, round: number): number {
  return size / 2 ** (round + 1);
}

const ROUND_NAMES: Readonly<Record<number, { slug: string; label: string; short: string }>> = {
  1: { slug: 'finale', label: 'Finale', short: 'F' },
  2: { slug: 'halbfinale', label: 'Halbfinale', short: 'HF' },
  4: { slug: 'viertelfinale', label: 'Viertelfinale', short: 'VF' },
  8: { slug: 'achtelfinale', label: 'Achtelfinale', short: 'AF' },
  16: { slug: 'sechzehntelfinale', label: 'Sechzehntelfinale', short: 'SF' },
};

/**
 * The named knockout rounds, largest first, for a per-round mode editor.
 * The slugs match those baked into the `roundKey`, so a `bestOf` set against
 * one of these applies to that round.
 */
export const NAMED_KO_ROUNDS: readonly { slug: string; label: string }[] = [
  { slug: 'achtelfinale', label: 'Achtelfinale' },
  { slug: 'viertelfinale', label: 'Viertelfinale' },
  { slug: 'halbfinale', label: 'Halbfinale' },
  { slug: 'finale', label: 'Finale' },
];

/**
 * Names a round by how many matches it contains.
 *
 * These are labels, not rules — but the slug becomes part of the `roundKey`,
 * and therefore the key the tournament director uses to give the final a
 * different mode (`ko.finale` → Best of 9). The naming is documented so those
 * keys are predictable.
 */
export function roundNaming(size: number, round: number) {
  const matches = matchesInRound(size, round);
  const known = ROUND_NAMES[matches];

  if (known !== undefined) {
    return known;
  }

  const remaining = matches * 2;
  return {
    slug: `runde-der-letzten-${remaining}`,
    label: `Runde der letzten ${remaining}`,
    short: `L${remaining}`,
  };
}
